// Android Viewport Fix - Compatible with all Android devices
import { Capacitor } from "@capacitor/core";

export class AndroidViewportFix {
  private static isApplied = false;
  private static deviceInfo: any = null;

  static apply() {
    if (!Capacitor.isNativePlatform()) return;
    if (this.isApplied) return;
    // Initialize deviceInfo to prevent null reference errors
    this.deviceInfo = { type: "android-portrait" };
    // No need for orientation handlers since app is locked to portrait
    this.isApplied = true;
    return;

    /* COMMENTED OUT - KEEPING FOR REFERENCE ONLY

    // Detect device capabilities
    this.detectDeviceCapabilities();
    
    // Apply fixes based on device capabilities
    this.applyCompatibleFixes();
    
    // Monitor orientation changes (safe for all devices)
    this.setupOrientationHandler();

    this.isApplied = true;
    */
  }

  private static detectDeviceCapabilities() {
    const userAgent = navigator.userAgent;
    const screenHeight = window.screen.height;
    const windowHeight = window.innerHeight;
    const supportsViewportUnits = this.testViewportUnitSupport();
    const supportsSafeArea = this.testSafeAreaSupport();

    this.deviceInfo = {
      userAgent,
      screenHeight,
      windowHeight,
      supportsViewportUnits,
      supportsSafeArea,
      type: this.categorizeDevice(userAgent, screenHeight),
    };
  }

  private static categorizeDevice(
    userAgent: string,
    screenHeight: number,
  ): string {
    // Check for older Android versions
    if (
      userAgent.includes("Android 4.") ||
      userAgent.includes("Android 5.") ||
      userAgent.includes("Android 6.")
    ) {
      return "legacy";
    }

    // Check for mid-range Android versions
    if (
      userAgent.includes("Android 7.") ||
      userAgent.includes("Android 8.") ||
      userAgent.includes("Android 9.")
    ) {
      return "mid-range";
    }

    // Modern Android (10+)
    return "modern";
  }

  private static testViewportUnitSupport(): boolean {
    // Test if vh units work correctly
    const testElement = document.createElement("div");
    testElement.style.height = "100vh";
    testElement.style.position = "absolute";
    testElement.style.visibility = "hidden";
    document.body.appendChild(testElement);

    const vhHeight = testElement.offsetHeight;
    document.body.removeChild(testElement);

    // Compare with window.innerHeight
    const heightDifference = Math.abs(vhHeight - window.innerHeight);
    return heightDifference < 50; // Allow 50px tolerance
  }

  private static testSafeAreaSupport(): boolean {
    // Test if safe-area-inset CSS is supported
    const testElement = document.createElement("div");
    testElement.style.paddingTop = "env(safe-area-inset-top, 99px)";
    document.body.appendChild(testElement);

    const computedStyle = window.getComputedStyle(testElement);
    const paddingTop = computedStyle.paddingTop;
    document.body.removeChild(testElement);

    return paddingTop !== "99px";
  }

  private static applyCompatibleFixes() {
    // Always safe: Fix viewport meta tag
    this.fixViewportMeta();

    // Apply device-specific fixes
    switch (this.deviceInfo.type) {
      case "legacy":
        this.applyLegacyFixes();
        break;

      case "mid-range":
        this.applyMidRangeFixes();
        break;

      case "modern":
        this.applyModernOptimizations();
        break;
    }
  }

  private static applyLegacyFixes() {
    // For older devices like Moto G4 - more aggressive fixes
    const rootElement = document.getElementById("root");
    const htmlElement = document.documentElement;
    const bodyElement = document.body;

    if (rootElement && htmlElement && bodyElement) {
      // Calculate status bar height for legacy devices
      const statusBarHeight = this.calculateStatusBarHeight();

      // Use percentage-based heights for older WebView
      htmlElement.style.height = "100%";
      htmlElement.style.overflow = "hidden";

      bodyElement.style.height = "100%";
      bodyElement.style.overflow = "hidden";
      bodyElement.style.position = "fixed";
      bodyElement.style.top = "0";
      bodyElement.style.left = "0";
      bodyElement.style.right = "0";
      bodyElement.style.bottom = "0";

      // Account for status bar space on legacy devices
      rootElement.style.height = `calc(100% - ${statusBarHeight}px)`;
      rootElement.style.minHeight = `calc(100% - ${statusBarHeight}px)`;
      rootElement.style.maxHeight = `calc(100% - ${statusBarHeight}px)`;
      rootElement.style.marginTop = `${statusBarHeight}px`;
      rootElement.style.overflow = "hidden";

      // Force layout recalculation for older WebView
      setTimeout(() => this.forceLayoutRecalculation(), 300);
    }
  }

  private static applyMidRangeFixes() {
    // For mid-range devices - balanced approach
    const rootElement = document.getElementById("root");

    if (rootElement) {
      const statusBarHeight = this.calculateStatusBarHeight();

      if (this.deviceInfo.supportsViewportUnits) {
        // Use viewport units but account for status bar
        rootElement.style.height = `calc(100vh - ${statusBarHeight}px)`;
        rootElement.style.minHeight = `calc(100vh - ${statusBarHeight}px)`;
        rootElement.style.marginTop = `${statusBarHeight}px`;
      } else {
        // Fallback to percentage with status bar space
        rootElement.style.height = `calc(100% - ${statusBarHeight}px)`;
        rootElement.style.minHeight = `calc(100% - ${statusBarHeight}px)`;
        rootElement.style.marginTop = `${statusBarHeight}px`;
      }

      rootElement.style.overflow = "hidden";

      // Gentle layout recalculation
      setTimeout(() => this.forceLayoutRecalculation(), 100);
    }
  }

  private static applyModernOptimizations() {
    // For modern devices - minimal intervention but ensure status bar space
    const rootElement = document.getElementById("root");

    if (rootElement) {
      const statusBarHeight = this.calculateStatusBarHeight();

      // Modern devices should handle viewport units well, but ensure status bar space
      if (this.deviceInfo.supportsViewportUnits) {
        rootElement.style.height = `calc(100vh - ${statusBarHeight}px)`;
        rootElement.style.minHeight = `calc(100vh - ${statusBarHeight}px)`;
        rootElement.style.marginTop = `${statusBarHeight}px`;
      } else {
        // Fallback just in case
        rootElement.style.height = `calc(100% - ${statusBarHeight}px)`;
        rootElement.style.minHeight = `calc(100% - ${statusBarHeight}px)`;
        rootElement.style.marginTop = `${statusBarHeight}px`;
      }

      // Only minimal layout adjustment if needed
      const heightDiff = Math.abs(
        rootElement.offsetHeight - (window.innerHeight - statusBarHeight),
      );
      if (heightDiff > 20) {
        setTimeout(() => this.forceLayoutRecalculation(), 50);
      }
    }
  }

  private static calculateStatusBarHeight(): number {
    // Calculate status bar height based on device and screen density
    const screenHeight = window.screen.height;
    const windowHeight = window.innerHeight;
    const devicePixelRatio = window.devicePixelRatio || 1;

    // Estimate status bar height based on device characteristics
    let statusBarHeight = 24; // Default fallback

    if (this.deviceInfo.type === "legacy") {
      // Older devices typically have 24-25dp status bars
      statusBarHeight = Math.round((24 * devicePixelRatio) / devicePixelRatio); // 24dp
    } else if (this.deviceInfo.type === "mid-range") {
      // Mid-range devices might have slightly taller status bars
      statusBarHeight = Math.round((25 * devicePixelRatio) / devicePixelRatio); // 25dp
    } else {
      // Modern devices often have taller status bars due to notches/camera cutouts
      if (screenHeight > 2000) {
        statusBarHeight = 44; // Tall modern devices
      } else {
        statusBarHeight = 24; // Standard modern devices
      }
    }

    // Additional detection based on actual height difference
    const heightDifference = screenHeight - windowHeight;
    if (heightDifference > 0 && heightDifference < 100) {
      // Use actual measured difference if it seems reasonable
      statusBarHeight = Math.max(statusBarHeight, heightDifference);
    }

    return statusBarHeight;
  }

  private static fixViewportMeta() {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      // Conservative viewport settings that work on all devices
      let viewportContent =
        "width=device-width, initial-scale=1.0, user-scalable=no";

      // Add viewport-fit only for devices that support it
      if (
        this.deviceInfo.supportsSafeArea ||
        this.deviceInfo.type === "modern"
      ) {
        viewportContent += ", viewport-fit=cover";
      }

      // Add shrink-to-fit for older devices that need it
      if (this.deviceInfo.type === "legacy") {
        viewportContent += ", shrink-to-fit=no";
      }

      viewport.setAttribute("content", viewportContent);
    }
  }

  private static fixRootHeight() {
    // This method is now handled in applyCompatibleFixes
    // Keeping for backward compatibility but not used
  }

  private static setupOrientationHandler() {
    const handleOrientationChange = () => {
      setTimeout(() => {
        this.forceLayoutRecalculation();
      }, 200);
    };

    window.addEventListener("orientationchange", handleOrientationChange);
    window.addEventListener("resize", handleOrientationChange);
  }

  private static forceLayoutRecalculation() {
    const rootElement = document.getElementById("root");
    if (rootElement) {
      // Gentle layout recalculation that works on all devices
      const originalHeight = rootElement.style.height;

      // Use requestAnimationFrame for smooth transition
      requestAnimationFrame(() => {
        rootElement.style.height =
          "calc(" + (originalHeight || "100%") + " - 1px)";

        requestAnimationFrame(() => {
          rootElement.style.height = originalHeight || "100%";

          // Dispatch resize event
          window.dispatchEvent(new Event("resize"));
        });
      });
    }
  }
}
